import { ADMIN_CLIENT, OEM_CLIENT } from '../constants/common';
import axios from '../utils/axios';

import store from '../app/store';
import authStorage from '../utils/authStorage';
import { handleSessionExpired } from './session';
import { loginSuccess, logout, selectUserInfo, selectUserProfile, updateUserInfo } from '../features/common/authSlice';

let userInfoPromise = null;

const clearUserInfoCache = () => {
  userInfoPromise = null;
};

const fetchUserInfoFromApi = async () => {
  const token = authStorage.get('token');
  if (!token) {
    return null;
  }

  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  const response = await axios.get('/user');

  if (!response?.data) {
    return null;
  }

  store.dispatch(updateUserInfo(response.data));
  return response.data;
};

const checkAuth = () => {
  const TOKEN = authStorage.get('token');
  if (!TOKEN) {
    return { isAuthenticated: false, token: null };
  }

  axios.defaults.headers.common['Authorization'] = `Bearer ${TOKEN}`;
  return { isAuthenticated: true, token: TOKEN };
};

const loginUser = async (email, password) => {
  try {
    const response = await axios.post('/login', { email, password });

    const { token, roles, service_access, is_gsc_optional } = response?.data || {};

    const hasRequiredRole = roles.some((role) => role === ADMIN_CLIENT || role === OEM_CLIENT);
    if (!hasRequiredRole) {
      const errorMessage = 'Invalid email or password. Please try again.';
      return { success: false, errorMessage };
    }

    authStorage.set('token', token);
    authStorage.setJSON('roles', roles);
    authStorage.set('is_gsc_optional', is_gsc_optional);

    if (service_access) {
      authStorage.setJSON('serviceAccess', service_access);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const { token: _token, ...userInfo } = response.data;

    store.dispatch(
      loginSuccess({
        token,
        service_access,
        userInfo,
      })
    );

    return { success: true, token };
  } catch (error) {
    let errorMessage = 'Login failed. Please try again.';
    if (error.response?.status === 422 && error.response?.data?.errors) {
      const errors = error.response.data.errors;
      errorMessage = Object.values(errors).flat().join(' ');
    } else {
      errorMessage = error.response?.data?.message || error.message || errorMessage;
    }
    return { success: false, errorMessage };
  }
};

const registerUser = async (name, email, password, confirmPassword, phone_number, industry_type) => {
  if (password !== confirmPassword) {
    return { success: false, errorMessage: 'Passwords do not match!' };
  }

  try {
    const response = await axios.post('/register', {
      name,
      email,
      password,
      password_confirmation: confirmPassword,
      phone_number,
      industry_type,
    });

    const { token } = response.data;

    authStorage.set('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    return { success: true, token };
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
    return { success: false, errorMessage };
  }
};

const logoutUser = async () => {
  const token = authStorage.get('token');

  clearUserInfoCache();
  delete axios.defaults.headers.common.Authorization;
  store.dispatch(logout());

  if (token) {
    axios.post('/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }

  handleSessionExpired();
  return { isAuthenticated: false, token: null };
};

const getUserInfo = async ({ force = false } = {}) => {
  try {
    const token = authStorage.get('token');
    if (!token) {
      return null;
    }

    const cached = selectUserInfo(store.getState());
    if (!force && cached) {
      return cached;
    }

    if (userInfoPromise) {
      return userInfoPromise;
    }

    userInfoPromise = fetchUserInfoFromApi().finally(() => {
      userInfoPromise = null;
    });

    return userInfoPromise;
  } catch (error) {
    console.error('Error fetching user info:', error);

    if (error.response?.status === 401) {
      clearUserInfoCache();
      handleSessionExpired();
    }

    return null;
  }
};

const getCachedUserProfile = async () => {
  const profile = selectUserProfile(store.getState());
  if (profile?.id) {
    return profile;
  }

  const info = await getUserInfo();
  return info?.user ?? null;
};

const requestPasswordReset = async (email, currentLanguage) => {
  try {
    await axios.post('/forgot-password-client', { email, lang: currentLanguage });
    return { success: true };
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Reset Password Request failed. Please try again.';
    return { success: false, errorMessage };
  }
};

const resetPassword = async (password, password_confirmation, email, token) => {
  try {
    await axios.post('/reset-password', { password, password_confirmation, email, token });
    return { success: true };
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Reset Password failed. Please try again.';
    return { success: false, errorMessage };
  }
};

const cahceClear = async () => {
  try {
    await axios.get('/clear-all-cache');
    return { success: true };
  } catch (error) {
    console.log(error);

    return { success: false };
  }
};

export {
  cahceClear,
  checkAuth,
  getCachedUserProfile,
  getUserInfo,
  loginUser,
  logoutUser,
  registerUser,
  requestPasswordReset,
  resetPassword,
};
